/**
 * Cosmetic ship variants — pure color presets, no physics impact.
 * Locked by score-threshold OR achievement-id. The store key is the
 * variant `id`; unknown / locked ids fall back to "default".
 */

export interface ShipVariant {
  id: string;
  name: string;
  hullEmissive: number;
  hullColor: number;
  wingEmissive: number;
  discColor: number;
  haloColor: number;
  unlock: { kind: "always" } | { kind: "score"; value: number } | { kind: "achievement"; id: string };
}

export const SHIP_VARIANTS: ShipVariant[] = [
  {
    id: "default",
    name: "Aether",
    hullColor: 0x121826,
    hullEmissive: 0x00e0ff,
    wingEmissive: 0xff3df0,
    discColor: 0x00e0ff,
    haloColor: 0xff3df0,
    unlock: { kind: "always" },
  },
  {
    id: "vermilion",
    name: "Vermilion",
    hullColor: 0x1a0e08,
    hullEmissive: 0xff8a32,
    wingEmissive: 0xff3060,
    discColor: 0xff8a32,
    haloColor: 0xff5b3a,
    unlock: { kind: "score", value: 1000 },
  },
  {
    id: "cyan_reach",
    name: "Cyan Reach",
    hullColor: 0x041214,
    hullEmissive: 0x1affea,
    wingEmissive: 0x60aaff,
    discColor: 0x1affea,
    haloColor: 0xaaffea,
    unlock: { kind: "score", value: 5000 },
  },
  {
    id: "magnetar",
    name: "Magnetar",
    hullColor: 0x120218,
    hullEmissive: 0xff00ff,
    wingEmissive: 0x00ff88,
    discColor: 0xff00ff,
    haloColor: 0x00ff88,
    unlock: { kind: "score", value: 10000 },
  },
  {
    id: "wraith",
    name: "Wraith",
    hullColor: 0x000000,
    hullEmissive: 0xffffff,
    wingEmissive: 0xcccccc,
    discColor: 0xeeeeee,
    haloColor: 0xffffff,
    unlock: { kind: "achievement", id: "streak_25" },
  },
];

export function getVariant(id: string): ShipVariant {
  return SHIP_VARIANTS.find((v) => v.id === id) ?? SHIP_VARIANTS[0];
}

export function isUnlocked(v: ShipVariant, bestScore: number, earned: Set<string>): boolean {
  switch (v.unlock.kind) {
    case "always": return true;
    case "score": return bestScore >= v.unlock.value;
    case "achievement": return earned.has(v.unlock.id);
  }
}

export function unlockedVariants(bestScore: number, earned: Set<string>): ShipVariant[] {
  return SHIP_VARIANTS.filter((v) => isUnlocked(v, bestScore, earned));
}
